import { createContext, Dispatch, SetStateAction } from 'react';

import { CalendarGap } from '@atom/components/workOrders/workOrdersCalendar/weekViewUtils';
import { AssetDetail, SearchAsset } from '@atom/types/asset';
import { GeoJSONGeometry } from '@atom/types/geojson';
import { InventoryAssetDetailType } from '@atom/types/inventory';
import { Task } from '@atom/types/task';
import { Summary, WorkOrderBulkField, WorkOrderType } from '@atom/types/work';
import {
  SearchWorkTemplate,
  WorkTemplateListItem,
} from '@atom/types/workTemplate';

export enum ModalStep {
  TEMPLATE = 'TEMPLATE',
  DETAILS = 'DETAILS',
  TASKS = 'TASKS',
}

export type ModalStepType = {
  title: string;
  value: ModalStep;
};

export type WorkCreateType = 'SINGLE' | 'BULK';

export type WorkTemplateSelection =
  | WorkTemplateListItem
  | SearchWorkTemplate
  | null;

export type DraftTask = Task & {
  createdInModal?: boolean;
};

export type WorkOrderDraft = {
  mode: WorkCreateType;
  quantity: number;
  workTemplate: WorkTemplateSelection;
  workTemplateType: WorkOrderType;
  name: string;
  dueDate: Date;
  plannedStartDate: Date;
  assetId: string;
  asset: InventoryAssetDetailType | SearchAsset | AssetDetail | null;
  location: GeoJSONGeometry;
  isLocationBased: boolean;
  summary: Summary[];
  fields: WorkOrderBulkField[];
  fieldOrder: string[];
  tasks: DraftTask[];
  startedFromScratch: boolean;
  fromCalendarDate?: Date;
  fromCalendarGap?: CalendarGap;
};

type Context = {
  activeStep: ModalStep;
  setActiveStep: Dispatch<SetStateAction<ModalStep>>;
  STEPS_ARRAY: ModalStepType[];
  draft: WorkOrderDraft;
  setDraft: Dispatch<SetStateAction<WorkOrderDraft>>;
  updateDraftField: <K extends keyof WorkOrderDraft>(
    key: K,
    value: WorkOrderDraft[K],
  ) => void;
  workTemplateRequired: boolean;
  showModeToggle: boolean;
  assetLocked: boolean;
  onCreateWorkTemplate: () => void;
  onTemplateClick: (template: WorkTemplateSelection) => Promise<void>;
  createDisabled: boolean;
  setCreateDisabled: Dispatch<SetStateAction<boolean>>;
  taskTemplatePickerOpen: boolean;
  setTaskTemplatePickerOpen: Dispatch<SetStateAction<boolean>>;
  requiredFieldIds: Set<string>;
  setRequiredFieldIds: Dispatch<SetStateAction<Set<string>>>;
};

export const initialDraft: WorkOrderDraft = {
  mode: 'SINGLE',
  quantity: 1,
  workTemplate: null,
  workTemplateType: WorkOrderType.DEFAULT,
  name: '',
  dueDate: null,
  plannedStartDate: null,
  assetId: null,
  asset: null,
  location: null,
  isLocationBased: false,
  summary: [],
  fields: [],
  fieldOrder: [],
  tasks: [],
  startedFromScratch: false,
};

export const initialState: Context = {
  activeStep: ModalStep.TEMPLATE,
  setActiveStep: () => {},
  STEPS_ARRAY: [],
  draft: initialDraft,
  setDraft: () => {},
  updateDraftField: () => {},
  workTemplateRequired: false,
  showModeToggle: false,
  assetLocked: false,
  onCreateWorkTemplate: () => {},
  onTemplateClick: () => Promise.resolve(),
  createDisabled: false,
  setCreateDisabled: () => {},
  taskTemplatePickerOpen: false,
  setTaskTemplatePickerOpen: () => {},
  requiredFieldIds: new Set([]),
  setRequiredFieldIds: () => {},
};

const CreateWorkModalContext = createContext<Context>(initialState);

export default CreateWorkModalContext;
